import React, { useEffect, useMemo, useState } from 'react';

// ALPS — "Inbound Joint" (main_process=INBOUND) plan tree from Andes
// (planda_alps_prod.dat_alps_live_data_na), grain MAIN_PROCESS/SHIFT.
// Collapsible hierarchy with a date column; Actual vs Forecast with
// Difference (Unit / Percent), red-highlighted when negative.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function dmon(d) {
  if (!d || d.length < 10) return d || '';
  const y = +d.slice(0, 4), m = +d.slice(5, 7), dd = +d.slice(8, 10);
  const wd = new Date(d + 'T12:00:00').getDay();
  return `${WD[wd]}\n${MONTHS[m - 1]} ${dd}`;
}
// Next Monday strictly after today (PT); if today is Monday, the Monday a week out.
function nextMonday() {
  const p = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const dow = p.getDay(); // Sun=0 .. Mon=1
  const add = ((8 - dow) % 7) || 7;
  p.setDate(p.getDate() + add);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`;
}
const fmt = (v, opts) => {
  if (v == null || isNaN(v) || v === 'RATE_NA') return v === 'RATE_NA' ? 'n/a' : '';
  const o = opts || {};
  if (o.pct) return `${(v).toFixed(1)}%`;
  if (o.dp != null) return v.toLocaleString(undefined, { minimumFractionDigits: o.dp, maximumFractionDigits: o.dp });
  return Math.round(v).toLocaleString();
};

// Tree definition. Each node: key, label, depth, optional concept+aspect to
// pull a value, or a computed kind. `diff` nodes compute Actual-Forecast.
// group nodes are just collapsible headers.
// kinds: 'group' | 'value' | 'diffUnit' | 'diffPct'
// A 'value' node reads pivot[concept][aspect].
const TREE = [
  { key: 'volume', label: 'Volume', depth: 0, kind: 'group', children: [
    { key: 'capacity', label: 'Capacity', depth: 1, kind: 'group', open: true, children: [
      { key: 'max_cartons', label: 'Max Cartons', depth: 2, kind: 'value', concept: 'MAX_CARTON_VOLUME', aspect: 'CONSTRAINT' },
      { key: 'assigned_cartons', label: 'Assigned Cartons', depth: 2, kind: 'value', concept: 'ASSIGNMENT_COMBINED_CARTONS', aspect: 'FORECAST' },
      { key: 'capability_comb', label: 'Capability (Combined)', depth: 2, kind: 'value', concept: 'CAPABILITY_COMBINED_CARTONS', aspect: 'FORECAST' },
      { key: 'capability_online', label: 'Capability (Online)', depth: 2, kind: 'value', concept: 'CAPABILITY_ONLINE_CARTONS', aspect: 'FORECAST' },
    ] },
    { key: 'assignments', label: 'Assignments', depth: 1, kind: 'group', children: [
      { key: 'assign_comb', label: 'Combined', depth: 2, kind: 'value', concept: 'ASSIGNMENT_COMBINED_CARTONS', aspect: 'FORECAST' },
      { key: 'assign_online', label: 'Online', depth: 2, kind: 'value', concept: 'ASSIGNMENT_ONLINE_CARTONS', aspect: 'FORECAST' },
    ] },
    { key: 'voldelta', label: 'Volume Delta', depth: 1, kind: 'group', children: [
      { key: 'voldelta_comb', label: 'Combined', depth: 2, kind: 'value', concept: 'VOLUME_DELTA_COMBINED_CARTONS', aspect: 'FORECAST' },
      { key: 'voldelta_online', label: 'Online', depth: 2, kind: 'value', concept: 'VOLUME_DELTA_ONLINE_CARTONS', aspect: 'FORECAST' },
    ] },
    // Pallet Cartons = Combined - PID cartons; HQ Pallets = ASSIGNMENT_PALLETS;
    // CPP = Pallet Cartons / HQ Pallets.
    { key: 'pallet_cartons', label: 'Pallet Cartons', depth: 1, kind: 'subConcept', a: 'ASSIGNMENT_COMBINED_CARTONS', b: 'ASSIGNMENT_PID_CARTONS', aspect: 'FORECAST' },
    { key: 'hq_pallets', label: 'HQ Pallets', depth: 1, kind: 'value', concept: 'ASSIGNMENT_PALLETS', aspect: 'FORECAST', dp: 0 },
    { key: 'cpp', label: 'CPP', depth: 1, kind: 'ratio', num: 'pallet_cartons', den: 'hq_pallets', dp: 2 },
  ] },
  { key: 'backlog', label: 'Backlog (End of Shift)', depth: 0, kind: 'value', concept: 'BACKLOG', aspect: 'FORECAST' },
  { key: 'rate', label: 'Rate', depth: 0, kind: 'group', children: [
    { key: 'cplh', label: 'CPLH', depth: 1, kind: 'value', concept: 'CPLH', aspect: 'FORECAST', rate: true },
    { key: 'tph', label: 'Diluted Rate (TPH)', depth: 1, kind: 'value', concept: 'TPH', aspect: 'FORECAST', rate: true },
    { key: 'derated', label: 'Derated Rate', depth: 1, kind: 'value', concept: 'DERATED_RATE', aspect: 'FORECAST', rate: true },
    { key: 'vetcplh', label: 'Veteran Rate (CPLH)', depth: 1, kind: 'value', concept: 'VETERAN_RATE_CPLH', aspect: 'FORECAST', rate: true },
  ] },
  { key: 'wad', label: 'Weighted Average Density', depth: 0, kind: 'group', children: [
    { key: 'wad_val', label: 'WAD', depth: 1, kind: 'value', concept: 'WEIGHTED_AVERAGE_DENSITY', aspect: 'FORECAST', rate: true, dp: 2 },
  ] },
  { key: 'hours', label: 'Hours', depth: 0, kind: 'group', open: true, children: [
    { key: 'needed', label: 'Needed Hours', depth: 1, kind: 'value', concept: 'SHOW_HOURS_NEEDED', aspect: 'FORECAST' },
    { key: 'show', label: 'Show Hours', depth: 1, kind: 'group', open: true, children: [
      { key: 'show_act', label: 'Actual', depth: 2, kind: 'value', concept: 'SHOW_HOURS', aspect: 'ACTUALS' },
      { key: 'show_fc', label: 'Forecast', depth: 2, kind: 'value', concept: 'SHOW_HOURS', aspect: 'FORECAST' },
      { key: 'show_du', label: 'Difference (Unit)', depth: 2, kind: 'diffUnit', a: 'show_act', b: 'show_fc' },
      { key: 'show_dp', label: 'Difference (Percent)', depth: 2, kind: 'diffPct', a: 'show_act', b: 'show_fc' },
      { key: 'scheduled', label: 'Scheduled', depth: 2, kind: 'value', concept: 'SCHEDULED_HOURS', aspect: 'FORECAST' },
      { key: 'absence', label: 'Absence Forecast', depth: 2, kind: 'value', concept: 'ABSENCE_HOURS', aspect: 'FORECAST' },
      { key: 'vet', label: 'VET', depth: 2, kind: 'value', concept: 'VOLUNTARY_EXTRA_TIME_HOURS', aspect: 'FORECAST' },
      { key: 'vto', label: 'VTO', depth: 2, kind: 'value', concept: 'VOLUNTARY_TIME_OFF_HOURS', aspect: 'FORECAST' },
    ] },
  ] },
];

function flatten(nodes, acc = []) { nodes.forEach((n) => { acc.push(n); if (n.children) flatten(n.children, acc); }); return acc; }
const ALL = flatten(TREE);

export default function Alps({ api, site }) {
  const [raw, setRaw] = useState(null);
  const [shift, setShift] = useState('DAY_SHIFT');
  const [from, setFrom] = useState('');  // set once data loads (earliest avail)
  const [to, setTo] = useState('');      // set once data loads (next Monday)
  // collapse state keyed by node.key; default from node.open
  const [open, setOpen] = useState(() => {
    const o = {}; ALL.forEach((n) => { if (n.kind === 'group') o[n.key] = !!n.open; }); return o;
  });

  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ALPS_${site}&shift_type=data`)
      .then((r) => r.json())
      .then((d) => setRaw(d && Array.isArray(d.rows) ? d : { rows: [] }))
      .catch(() => setRaw({ rows: [] }));
  }, [api, site]);

  const availDates = useMemo(() => {
    const s = new Set(((raw && raw.rows) || []).map((r) => r.date));
    return [...s].sort();
  }, [raw]);

  // Default range once data loads: earliest available -> next Monday.
  useEffect(() => {
    if (!availDates.length) return;
    const nm = nextMonday();
    if (!from) setFrom(availDates[0]);
    if (!to) setTo(nm);
  }, [availDates]); // eslint-disable-line

  // Dates shown = available dates within [from, to].
  const dates = useMemo(
    () => availDates.filter((d) => (!from || d >= from) && (!to || d <= to)),
    [availDates, from, to]
  );

  // pivot: { process: { date: { concept: { aspect: value } } } } for the
  // selected shift (COMBINED sums Day+Night). Both processes are built so they
  // can be rendered stacked.
  const pivot = useMemo(() => {
    const p = {};
    for (const r of (raw && raw.rows) || []) {
      const proc = r.mainProcess || 'INBOUND';
      if (shift !== 'COMBINED' && r.shift !== shift) continue;
      if (r.value == null) continue;
      const pp = (p[proc] = p[proc] || {});
      const day = (pp[r.date] = pp[r.date] || {});
      const bucket = (day[r.concept] = day[r.concept] || {});
      bucket[r.aspect] = (bucket[r.aspect] || 0) + r.value;
    }
    return p;
  }, [raw, shift]);

  // Resolve a node's numeric value for a given process + date.
  const valueOf = (node, proc, date) => {
    const pv = ((pivot[proc] || {})[date]) || {};
    if (node.kind === 'value') {
      if (node.rate && shift === 'COMBINED') return 'RATE_NA';
      let v = (pv[node.concept] || {})[node.aspect];
      if ((v == null) && node.fallback) {
        for (const fb of node.fallback) {
          const [c, a] = fb.split(':');
          if ((pv[c] || {})[a] != null) { v = pv[c][a]; break; }
        }
      }
      return v == null ? null : v;
    }
    if (node.kind === 'diffUnit' || node.kind === 'diffPct') {
      const a = valueOf(ALL.find((n) => n.key === node.a), proc, date);
      const b = valueOf(ALL.find((n) => n.key === node.b), proc, date);
      if (a == null || a === 'RATE_NA' || b == null || b === 'RATE_NA') return null;
      if (node.kind === 'diffUnit') return a - b;
      return b ? ((a - b) / b) * 100 : null;
    }
    if (node.kind === 'subConcept') {
      // a - b, both from the pivot at this node's aspect
      const a = (pv[node.a] || {})[node.aspect];
      const b = (pv[node.b] || {})[node.aspect];
      if (a == null || b == null) return null;
      return a - b;
    }
    if (node.kind === 'ratio') {
      const n = valueOf(ALL.find((x) => x.key === node.num), proc, date);
      const d = valueOf(ALL.find((x) => x.key === node.den), proc, date);
      if (n == null || d == null || !d) return null;
      return n / d;
    }
    return null;
  };

  // Is a node visible (all ancestors open)?
  const visible = (node) => {
    // walk the tree to find ancestors
    let path = [];
    const find = (nodes, trail) => {
      for (const n of nodes) {
        if (n.key === node.key) { path = trail; return true; }
        if (n.children && find(n.children, [...trail, n])) return true;
      }
      return false;
    };
    find(TREE, []);
    return path.every((a) => open[a.key]);
  };

  const cell = { border: '1px solid #ddd', padding: '3px 10px', fontSize: '0.8rem' };
  const valCell = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const shiftLabel = shift === 'DAY_SHIFT' ? 'Day' : shift === 'NIGHT_SHIFT' ? 'Night' : 'Combined (Day + Night)';

  const procTable = (proc, procLabel) => (
    <div className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 12 }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...cell, background: '#0f1b2d', color: '#fff', textAlign: 'left', minWidth: 220, position: 'sticky', left: 0 }}>{procLabel}</th>
            {dates.map((d) => (
              <th key={d} style={{ ...cell, background: '#0f1b2d', color: '#fff', textAlign: 'right', whiteSpace: 'pre-line', minWidth: 72 }}>{dmon(d)}</th>
            ))}
          </tr>
          <tr>
            <td style={{ ...cell, background: '#1b2b44', color: '#fff', fontWeight: 700, position: 'sticky', left: 0 }} colSpan={dates.length + 1}>
              {shiftLabel}
            </td>
          </tr>
        </thead>
        <tbody>
          {ALL.filter((n) => n.depth === 0 || visible(n)).map((n) => {
            const isGroup = n.kind === 'group';
            const indent = 6 + n.depth * 16;
            const bg = n.depth === 0 ? '#c9d4e3' : n.depth === 1 ? '#e8edf4' : '#fff';
            return (
              <tr key={n.key}>
                <td
                  style={{ ...cell, paddingLeft: indent, background: bg, cursor: isGroup ? 'pointer' : 'default', fontWeight: n.depth <= 1 ? 600 : 400, position: 'sticky', left: 0 }}
                  onClick={isGroup ? () => setOpen((o) => ({ ...o, [n.key]: !o[n.key] })) : undefined}
                >
                  {isGroup ? (open[n.key] ? '▾ ' : '▸ ') : ''}{n.label}
                </td>
                {dates.map((d) => {
                  const v = isGroup ? null : valueOf(n, proc, d);
                  const isDiff = n.kind === 'diffUnit' || n.kind === 'diffPct';
                  const neg = isDiff && v != null && v !== 'RATE_NA' && v < 0;
                  const fmtOpts = n.kind === 'diffPct' ? { pct: true } : (n.dp != null ? { dp: n.dp } : undefined);
                  return (
                    <td key={d} style={{ ...valCell, background: neg ? '#fdd9d7' : bg, color: neg ? '#c0392b' : (v === 'RATE_NA' ? '#999' : '#000') }} title={v === 'RATE_NA' ? 'Rates are not summable across shifts — pick Day or Night.' : undefined}>
                      {isGroup ? '' : (v === 'RATE_NA' ? 'n/a' : fmt(v, fmtOpts))}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <h2 className="section-title">ALPS — {site}</h2>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>From:{' '}
          <input type="date" value={from} min={availDates[0]} max={availDates[availDates.length - 1]} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.75rem' }} />
        </label>
        <label style={{ fontSize: '0.75rem' }}>To:{' '}
          <input type="date" value={to} min={availDates[0]} max={availDates[availDates.length - 1]} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.75rem' }} />
        </label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={(e) => setShift(e.target.value)} style={{ fontSize: '0.75rem' }}>
            <option value="DAY_SHIFT">Day</option>
            <option value="NIGHT_SHIFT">Night</option>
            <option value="COMBINED">Combined (Day + Night)</option>
          </select>
        </label>
        <span style={{ fontSize: '0.7rem', color: '#555' }}>{dates.length} day(s)</span>
        {raw && raw.generatedAt && <span style={{ fontSize: '0.65rem', color: '#888' }}>synced hourly · last {String(raw.generatedAt).slice(0, 16).replace('T', ' ')}Z</span>}
      </div>

      {procTable('INBOUND', 'Inbound Joint')}
      {procTable('DA', 'DA')}
    </div>
  );
}
